import React, { useContext, useState, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { Area } from '../types';
import Card from '../components/common/Card';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, MagnifyingGlassIcon, MapPinIcon, ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';


const EditAreaModal: React.FC<{
    area: Area | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (area: Area) => void;
}> = ({ area, isOpen, onClose, onSave }) => {
    const [formData, setFormData] = useState<Area | null>(null);

    React.useEffect(() => {
        setFormData(area);
    }, [area]);

    if (!isOpen || !formData) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md relative animate-in fade-in zoom-in duration-200">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
                    <XMarkIcon className="h-6 w-6" />
                </button>
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Editar Área</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre del Área</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-gray-50 focus:bg-white transition-colors" required />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción (Opcional)</label>
                        <textarea name="description" value={formData.description || ''} onChange={handleChange} rows={3} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-gray-50 focus:bg-white transition-colors resize-none" placeholder="Ej: Nave principal de envasado y etiquetado" />
                    </div>
                    <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-gray-100">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
                        <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const AdminAreas: React.FC = () => {
    const { areas, addArea, deleteArea, updateArea, meters } = useContext(AppContext);
    const [newArea, setNewArea] = useState<Omit<Area, 'id'>>({ name: '', description: '' });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingArea, setEditingArea] = useState<Area | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setNewArea(prev => ({ ...prev, [name]: value }));
    };

    const handleAddArea = (e: React.FormEvent) => {
        e.preventDefault();
        if (newArea.name.trim()) {
            addArea({ name: newArea.name.trim(), description: newArea.description?.trim() });
            setNewArea({ name: '', description: '' });
            setIsCreateModalOpen(false);
        }
    };

    const handleOpenEditModal = (area: Area) => {
        setEditingArea(area);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditingArea(null);
        setIsEditModalOpen(false);
    };

    const handleSaveChanges = (updatedArea: Area) => {
        updateArea(updatedArea);
        handleCloseEditModal();
    };

    const handleDeleteArea = (areaId: string, areaName: string) => {
        // Check if area is used by any meter
        const isUsed = meters.some(m => m.area === areaName || m.area === areaId);
        if (isUsed) {
            alert(`No se puede eliminar el área "${areaName}" porque existen medidores asignados a ella. Reasigne los medidores primero.`);
            return;
        }

        if (window.confirm(`¿Está seguro de eliminar el área "${areaName}"?`)) {
            deleteArea(areaId);
        }
    }

    const filteredAreas = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return areas;
        return areas.filter(a =>
            a.name.toLowerCase().includes(term) ||
            (a.description && a.description.toLowerCase().includes(term))
        );
    }, [areas, searchTerm]);

    return (
        <div className="w-full space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Infraestructura: Áreas</h1>
                    <p className="text-gray-500 mt-1">Gestiona los sectores y áreas físicas de la planta productiva.</p>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="w-full sm:w-auto flex justify-center items-center py-2.5 px-5 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-secondary hover:shadow-lg transform active:scale-[0.98] transition-all"
                >
                    <PlusIcon className="h-5 w-5 mr-2 stroke-2" />
                    Nueva Área
                </button>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center w-full sm:w-96 bg-gray-50 rounded-xl px-3 border border-transparent focus-within:ring-2 focus-within:ring-brand-primary/20 focus-within:border-brand-primary transition-all">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar área por nombre o descripción..."
                        className="w-full bg-transparent border-none focus:ring-0 p-2.5 text-gray-700 placeholder-gray-400 text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="hidden sm:flex bg-gray-100 p-1 rounded-xl">
                    <button
                        onClick={() => setViewMode('table')}
                        className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        title="Vista de Tabla"
                    >
                        <ListBulletIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
                        title="Vista de Tarjetas"
                    >
                        <Squares2X2Icon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* MOBILE ALL-TIME CARD VIEW */}
            <div className="sm:hidden grid grid-cols-1 gap-4">
                {filteredAreas.length === 0 ? (
                    <div className="col-span-full text-center py-12 px-4 bg-white rounded-2xl border border-dashed border-gray-300 shadow-sm">
                        <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 font-bold text-lg">No se encontraron áreas</p>
                        <p className="text-gray-400 text-sm mt-1">Registra sectores de la planta para organizar tus equipos.</p>
                    </div>
                ) : (
                    filteredAreas.map(area => {
                        const metersInArea = meters.filter(m => m.area === area.name || m.area === area.id).length;
                        return (
                            <Card key={area.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group overflow-hidden">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="space-y-1">
                                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                            <MapPinIcon className="h-5 w-5 mr-1.5 text-gray-400" />
                                            {area.name}
                                        </h3>

                                    </div>
                                    <div className="flex space-x-1 bg-white/90 p-1 rounded-lg border border-gray-100 shadow-sm transition-all duration-200">
                                        <button onClick={() => handleOpenEditModal(area)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => handleDeleteArea(area.id, area.name)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-500 mb-4 line-clamp-3 mt-2">{area.description || 'Sin descripción referencial.'}</p>

                                <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Equipos Asignados</span>
                                    <span className="bg-amber-50 text-brand-primary font-black py-1 px-3 rounded-lg border border-amber-100">{metersInArea}</span>
                                </div>
                            </Card>
                        )
                    })
                )}
            </div>

            {/* DESKTOP VIEW */}
            <div className="hidden sm:block">
                {viewMode === 'table' ? (
                    <Card className="overflow-hidden p-0 shadow-sm border border-gray-100">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre del Área</th>
                                        <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Descripción</th>
                                        <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Equipos</th>
                                        <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {filteredAreas.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center">
                                                <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                                <p className="text-gray-600 font-bold text-lg">No se encontraron áreas</p>
                                                <p className="text-gray-400 text-sm mt-1">Registra sectores de la planta para organizar tus equipos.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredAreas.map(area => {
                                            const metersInArea = meters.filter(m => m.area === area.name || m.area === area.id).length;
                                            return (
                                                <tr key={area.id} className="hover:bg-amber-50/50 transition-colors">
                                                    <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <MapPinIcon className="h-5 w-5 text-brand-primary mr-3" />
                                                            <div>
                                                                <div className="text-sm font-bold text-gray-900">{area.name}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-500 line-clamp-1">{area.description || 'Sin descripción'}</div>
                                                    </td>
                                                    <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-brand-primary border border-amber-100">
                                                            {metersInArea} asignados
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <button onClick={() => handleOpenEditModal(area)} className="p-2 text-gray-400 hover:text-brand-primary hover:bg-amber-50 rounded-lg transition-colors mr-1" title="Editar">
                                                            <PencilIcon className="h-5 w-5" />
                                                        </button>
                                                        <button onClick={() => handleDeleteArea(area.id, area.name)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                                                            <TrashIcon className="h-5 w-5" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredAreas.length === 0 ? (
                            <div className="col-span-full text-center py-12 px-4 bg-white rounded-2xl border border-dashed border-gray-300 shadow-sm">
                                <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-600 font-bold text-lg">No se encontraron áreas</p>
                                <p className="text-gray-400 text-sm mt-1">Registra sectores de la planta para organizar tus equipos.</p>
                            </div>
                        ) : (
                            filteredAreas.map(area => {
                                const metersInArea = meters.filter(m => m.area === area.name || m.area === area.id).length;
                                return (
                                    <Card key={area.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group overflow-hidden">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="space-y-1">
                                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-brand-primary transition-colors flex items-center">
                                                    <MapPinIcon className="h-5 w-5 mr-1.5 text-gray-400 group-hover:text-brand-primary" />
                                                    {area.name}
                                                </h3>
                                            </div>
                                            <div className="flex opacity-100 sm:opacity-0 sm:group-hover:opacity-100 absolute top-4 right-4 space-x-1 bg-white/90 backdrop-blur-sm p-1 rounded-lg border border-gray-100 shadow-sm transition-all duration-200">
                                                <button onClick={() => handleOpenEditModal(area)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDeleteArea(area.id, area.name)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <p className="text-sm text-gray-500 mb-4 line-clamp-2 h-10">{area.description || 'Sin descripción referencial.'}</p>

                                        <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Equipos Asignados</span>
                                            <span className="bg-amber-50 text-brand-primary font-black py-1 px-3 rounded-lg border border-amber-100">{metersInArea}</span>
                                        </div>
                                    </Card>
                                )
                            })
                        )}
                    </div>
                )}
            </div>

            {/* Modal Creador de Áreas */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative animate-in fade-in zoom-in duration-200">
                        <button onClick={() => setIsCreateModalOpen(false)} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                        <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                            <PlusIcon className="h-7 w-7 mr-2 text-brand-primary" />
                            Nueva Área
                        </h2>
                        <form onSubmit={handleAddArea} className="space-y-5">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre</label>
                                <input type="text" name="name" value={newArea.name} onChange={handleInputChange} placeholder="Ej: Sala de Compresores" className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción (Opcional)</label>
                                <textarea name="description" value={newArea.description || ''} onChange={handleInputChange} placeholder="Breve referencia física o funcional del área..." rows={3} className="w-full border-gray-300 rounded-xl shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors resize-none" />
                            </div>

                            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                                <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
                                <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Registrar Área</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <EditAreaModal
                isOpen={isEditModalOpen}
                area={editingArea}
                onClose={handleCloseEditModal}
                onSave={handleSaveChanges}
            />
        </div>
    );
};

export default AdminAreas;
